import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Avatar,
  AvatarGroup,
  Divider,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  Grid,
  Badge,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import ReplayIcon from "@mui/icons-material/Replay";
import CancelIcon from "@mui/icons-material/Cancel";
import LaunchIcon from "@mui/icons-material/Launch";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import PrecisionManufacturingIcon from "@mui/icons-material/PrecisionManufacturing";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import QueryBuilderIcon from "@mui/icons-material/QueryBuilder";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import SnoozeIcon from "@mui/icons-material/Snooze";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";

dayjs.extend(relativeTime);

const statusOptions = [
  { label: "All", value: "all" },
  { label: "Running", value: "running" },
  { label: "Queued", value: "queued" },
  { label: "Failed", value: "failed" },
  { label: "Done", value: "done" },
  { label: "Cancelled", value: "cancelled" },
];

const jobTypeOptions = [
  { label: "All", value: "" },
  { label: "Lead follow-up", value: "lead-followup" },
  { label: "Retargeting", value: "retargeting" },
  { label: "Daily Ops Summary", value: "daily-operations-summary" },
];

const stageOptions = [
  { label: "All stages", value: "" },
  { label: "Lead", value: "lead" },
  { label: "Customer", value: "customer" },
  { label: "Project", value: "project" },
  { label: "Estimate", value: "estimate" },
  { label: "Sales order", value: "sales_order" },
  { label: "Fulfillment", value: "fulfillment" },
];

const stageTooltips = {
  lead: "New inbound leads and qualification steps.",
  customer: "Active customers without linked projects.",
  project: "Projects awaiting estimates or action.",
  estimate: "Quotes that need to be sent or followed up.",
  sales_order: "Estimates that became orders awaiting conversion.",
  fulfillment: "Orders in production or handoff to operations.",
};

const modeOptions = [
  { label: "All modes", value: "" },
  { label: "Autonomous", value: "autonomous" },
  { label: "Draft", value: "draft" },
];

const stateOptions = [
  { label: "Open", value: "open" },
  { label: "Snoozed", value: "snoozed" },
  { label: "Resolved", value: "resolved" },
  { label: "All", value: "all" },
];

const statusColors = {
  running: "primary",
  queued: "info",
  failed: "error",
  done: "success",
  cancelled: "default",
};

const stateColors = {
  open: "default",
  snoozed: "info",
  resolved: "success",
};

const columnOptions = [
  { key: "status", label: "Status" },
  { key: "state", label: "State" },
  { key: "jobType", label: "Job Type" },
  { key: "stage", label: "Stage" },
  { key: "owner", label: "Owner" },
  { key: "mode", label: "Mode" },
  { key: "entity", label: "Entity" },
  { key: "output", label: "Output" },
  { key: "runAt", label: "Run at" },
  { key: "attempts", label: "Attempts" },
  { key: "email", label: "Email" },
  { key: "updated", label: "Updated" },
  { key: "actions", label: "Actions" },
];

const ColumnHeader = ({ label, tooltip }) => (
  <Stack direction="row" spacing={0.5} alignItems="center">
    <Typography variant="caption" fontWeight={700} textTransform="uppercase">
      {label}
    </Typography>
    {tooltip && (
      <Tooltip title={tooltip} arrow>
        <InfoOutlinedIcon fontSize="inherit" sx={{ fontSize: 14, color: "text.secondary" }} />
      </Tooltip>
    )}
  </Stack>
);

const MetricCard = ({ label, value, icon, tooltip, tone = "info" }) => (
  <Tooltip title={tooltip || label} arrow>
    <Paper className={`ai-metric-card tone-${tone}`} elevation={0}>
      <Stack direction="row" spacing={1} alignItems="center">
        {icon && <span className="ai-metric-icon">{icon}</span>}
        <div>
          <Typography variant="overline" color="textSecondary">
            {label}
          </Typography>
          <Typography variant="h6" fontWeight={800} color="text.primary">
            {value}
          </Typography>
        </div>
      </Stack>
    </Paper>
  </Tooltip>
);

function formatDateWithRelative(value) {
  if (!value) return "-";
  return `${dayjs(value).format("MMM D, h:mm A")} (${dayjs(value).fromNow()})`;
}

function prettyJson(value) {
  if (!value) return "";
  try {
    return JSON.stringify(value, null, 2);
  } catch (err) {
    return String(value);
  }
}

function joinMeta(parts = []) {
  return parts.filter(Boolean).join(" • ");
}

const LiveDot = ({ active }) => (
  <span className={`ai-live-dot ${active ? "active" : ""}`} />
);

const StatusPill = ({ status }) => (
  <Chip
    size="small"
    label={status}
    color={statusColors[status] || "default"}
    className={status === "running" ? "ai-job-status-running" : "ai-job-status"}
    icon={status === "running" ? <PrecisionManufacturingIcon fontSize="small" /> : undefined}
  />
);

const AssignedAvatars = ({ users }) => {
  if (!users || users.length === 0) return null;

  const initialsFor = (name) =>
    (name || "?")
      .split(" ")
      .filter(Boolean)
      .map((n) => n[0])
      .join("")
      .slice(0, 2)
      .toUpperCase();

  return (
    <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 0.5, flexWrap: "wrap" }}>
      <Typography variant="caption" color="textSecondary">
        Assigned to
      </Typography>
      <AvatarGroup
        max={4}
        spacing={8}
        sx={{ "& .MuiAvatar-root": { width: 26, height: 26, fontSize: 12 } }}
      >
        {users.map((user) => (
          <Tooltip key={user.id} title={`${user.name}${user.role ? " • " + user.role : ""}`}>
            <Avatar src={user.profilePicture || undefined} alt={user.name}>
              {initialsFor(user.name)}
            </Avatar>
          </Tooltip>
        ))}
      </AvatarGroup>
      <Typography variant="caption" color="textSecondary">
        {users.map((u) => u.name).filter(Boolean).join(", ")}
      </Typography>
    </Stack>
  );
};

function formatEntityTitle(job) {
  const meta = job.entityMetadata || {};
  if (meta.display) return meta.display;
  if (job.entityDisplay) return job.entityDisplay;
  if (meta.customer?.businessName) return meta.customer.businessName;
  if (meta.customer?.contactName) return meta.customer.contactName;
  if (meta.customer?.emailAddress) return meta.customer.emailAddress;
  if (job.entityId) return `${job.entityType} #${job.entityId}`;
  return job.entityType || "Entity";
}

function formatEntitySubtitle(job) {
  const meta = job.entityMetadata || {};
  if (meta.subtitle) return meta.subtitle;
  if (meta.customer?.contactName && meta.customer?.emailAddress) {
    return `${meta.customer.contactName} • ${meta.customer.emailAddress}`;
  }
  if (job.entityId) return `${job.entityType} #${job.entityId}`;
  return job.entityType || "-";
}

const EntityCell = ({ job }) => {
  const meta = job.entityMetadata || {};
  const customer = meta.customer;

  return (
    <Stack spacing={0.25}>
      <Typography fontWeight={700}>{formatEntityTitle(job)}</Typography>
      <Typography variant="body2" color="textSecondary">{formatEntitySubtitle(job)}</Typography>
      {customer && (
        <Typography variant="body2" color="textSecondary">
          {joinMeta([
            customer.status,
            customer.leadSource ? `Source: ${customer.leadSource}` : "",
          ])}
        </Typography>
      )}
      {customer?.emailAddress && (
        <Typography variant="body2" color="textSecondary">
          {customer.contactName ? `${customer.contactName} • ` : ""}
          {customer.emailAddress}
          {customer.phoneNumber ? ` • ${customer.phoneNumber}` : ""}
        </Typography>
      )}
      <AssignedAvatars users={meta.assignedUsers} />
    </Stack>
  );
};

const AiJobsTab = ({ instanceUniqueId }) => {
  const [jobs, setJobs] = useState([]);
  const [jobCount, setJobCount] = useState(0);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({ status: "running", jobType: "", search: "", stageKey: "", owner: "", mode: "", state: "open" });
  const [selectedJobId, setSelectedJobId] = useState(null);
  const [selectedJob, setSelectedJob] = useState(null);
  const [isDetailLoading, setIsDetailLoading] = useState(false);
  const [draftsJob, setDraftsJob] = useState(null);
  const [drafts, setDrafts] = useState([]);
  const [isDraftsLoading, setIsDraftsLoading] = useState(false);
  const [draftsError, setDraftsError] = useState(null);
  const [snoozeJob, setSnoozeJob] = useState(null);
  const [snoozeHours, setSnoozeHours] = useState(24);
  const [assignJob, setAssignJob] = useState(null);
  const [assignOwner, setAssignOwner] = useState("");
  const [visibleColumns, setVisibleColumns] = useState(() =>
    columnOptions.reduce((acc, col) => ({ ...acc, [col.key]: true }), {})
  );

  const isColumnVisible = useCallback((key) => visibleColumns[key] !== false, [visibleColumns]);

  const visibleColumnCount = useMemo(
    () => Object.values(visibleColumns).filter(Boolean).length || columnOptions.length,
    [visibleColumns]
  );

  const jobTypeChoices = useMemo(() => {
    const seen = new Set(jobTypeOptions.map((opt) => opt.value));
    const dynamic = jobs
      .map((job) => job.jobType)
      .filter(Boolean)
      .filter((jt) => !seen.has(jt));
    return jobTypeOptions.concat(dynamic.map((value) => ({ label: value, value })));
  }, [jobs]);

  const ownerChoices = useMemo(() => {
    const owners = new Map();
    jobs.forEach((job) => {
      if (job.ownerUser?.id && !owners.has(job.ownerUser.id)) {
        owners.set(job.ownerUser.id, job.ownerUser.name || `User ${job.ownerUser.id}`);
      }
    });
    return [{ label: "All owners", value: "" }].concat(
      Array.from(owners.entries()).map(([value, label]) => ({ label, value }))
    );
  }, [jobs]);

  const openCount = useMemo(
    () => jobs.filter((job) => (job.state || "open") === "open").length,
    [jobs]
  );
  const snoozedCount = useMemo(
    () => jobs.filter((job) => (job.state || "open") === "snoozed").length,
    [jobs]
  );
  const pendingApprovalsCount = useMemo(
    () => jobs.filter((job) => job.hasPendingDrafts || job.requiresApproval).length,
    [jobs]
  );

  const fetchSummary = useCallback(async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_NODEJS_SERVER_URL}/ai/instances/${instanceUniqueId}/jobs/summary`,
        {
          headers: { accessToken: localStorage.getItem("accessToken") },
        }
      );
      const payload = await response.json();
      if (payload?.success) {
        setSummary(payload.summary);
      }
    } catch (err) {
      // silent
    }
  }, [instanceUniqueId]);


  const fetchJobs = useCallback(
    async ({ silent } = {}) => {
      if (silent) {
        setIsRefreshing(true);
      } else {
        setLoading(true);
      }
      setError(null);
      try {
        const params = new URLSearchParams();
        params.set("status", filters.status);
        if (filters.jobType) params.set("jobType", filters.jobType);
        if (filters.search) params.set("search", filters.search);
        params.set("limit", "50");

        const response = await fetch(
          `${process.env.REACT_APP_NODEJS_SERVER_URL}/ai/instances/${instanceUniqueId}/jobs?${params.toString()}`,
          { headers: { accessToken: localStorage.getItem("accessToken") } }
        );

        const payload = await response.json();
        if (payload?.success) {
          setJobs(payload.rows || []);
          setJobCount(payload.count || 0);
          setHasLoaded(true);
        } else {
          setError(payload?.message || "Unable to load jobs right now.");
        }
      } catch (err) {
        setError("Unable to load jobs right now.");
      } finally {
        if (silent) {
          setIsRefreshing(false);
        } else {
          setLoading(false);
        }
      }
    },
    [filters, instanceUniqueId]
  );

  const fetchJobDetail = useCallback(
    async (jobId) => {
      if (!jobId) return;
      setIsDetailLoading(true);
      setSelectedJob(null);
      try {
        const response = await fetch(
          `${process.env.REACT_APP_NODEJS_SERVER_URL}/ai/instances/${instanceUniqueId}/jobs/${jobId}?includeRaw=true`,
          { headers: { accessToken: localStorage.getItem("accessToken") } }
        );
        const payload = await response.json();
        if (payload?.success) {
          setSelectedJob(payload.job);
        }
      } catch (err) {
        // noop
      } finally {
        setIsDetailLoading(false);
      }
    },
    [instanceUniqueId]
  );

  const handleRetry = async (jobId) => {
    try {
      await fetch(
        `${process.env.REACT_APP_NODEJS_SERVER_URL}/ai/instances/${instanceUniqueId}/jobs/${jobId}/retry`,
        {
          method: "POST",
          headers: { accessToken: localStorage.getItem("accessToken") },
        }
      );
    } catch (err) {
      // ignore
    } finally {
      fetchJobs();
      fetchSummary();
    }
  };

  const handleCancel = async (jobId) => {
    try {
      await fetch(
        `${process.env.REACT_APP_NODEJS_SERVER_URL}/ai/instances/${instanceUniqueId}/jobs/${jobId}/cancel`,
        {
          method: "POST",
          headers: { accessToken: localStorage.getItem("accessToken") },
        }
      );
    } catch (err) {
      // ignore
    } finally {
      fetchJobs();
      fetchSummary();
    }
  };

  const handleResolve = async (jobId) => {
    try {
      await fetch(`${process.env.REACT_APP_NODEJS_SERVER_URL}/ai/jobs/${jobId}/resolve`, {
        method: "POST",
        headers: { accessToken: localStorage.getItem("accessToken"), "Content-Type": "application/json" },
      });
    } catch (err) {
      // ignore
    } finally {
      fetchJobs();
      fetchSummary();
    }
  };

  const handleSnoozeSubmit = async () => {
    if (!snoozeJob) return;
    try {
      await fetch(`${process.env.REACT_APP_NODEJS_SERVER_URL}/ai/jobs/${snoozeJob.id}/snooze`, {
        method: "POST",
        headers: { accessToken: localStorage.getItem("accessToken"), "Content-Type": "application/json" },
        body: JSON.stringify({ hours: snoozeHours }),
      });
    } catch (err) {
      // ignore
    } finally {
      setSnoozeJob(null);
      fetchJobs();
      fetchSummary();
    }
  };

  const handleAssignSubmit = async () => {
    if (!assignJob || !assignOwner) return;
    try {
      await fetch(`${process.env.REACT_APP_NODEJS_SERVER_URL}/ai/jobs/${assignJob.id}/assign`, {
        method: "POST",
        headers: { accessToken: localStorage.getItem("accessToken"), "Content-Type": "application/json" },
        body: JSON.stringify({ ownerUserId: assignOwner }),
      });
    } catch (err) {
      // ignore
    } finally {
      setAssignJob(null);
      setAssignOwner("");
      fetchJobs();
      fetchSummary();
    }
  };

  const fetchDraftsForJob = useCallback(
    async (job) => {
      if (!job) return;
      setDraftsJob(job);
      setIsDraftsLoading(true);
      setDraftsError(null);
      try {
        const response = await fetch(
          `${process.env.REACT_APP_NODEJS_SERVER_URL}/ai/jobs/${job.id}/drafts`,
          { headers: { accessToken: localStorage.getItem("accessToken") } }
        );
        const payload = await response.json();
        if (payload?.success) {
          setDrafts(payload.drafts || []);
        } else {
          setDraftsError(payload?.message || "Unable to load drafts.");
        }
      } catch (err) {
        setDraftsError("Unable to load drafts.");
      } finally {
        setIsDraftsLoading(false);
      }
    },
    []
  );

  const handleApproveDraft = async (draftId) => {
    try {
      await fetch(`${process.env.REACT_APP_NODEJS_SERVER_URL}/ai/drafts/${draftId}/approve`, {
        method: "POST",
        headers: { accessToken: localStorage.getItem("accessToken") },
      });
      if (draftsJob) fetchDraftsForJob(draftsJob);
      fetchJobs();
    } catch (err) {
      // ignore
    }
  };

  const handleRejectDraft = async (draftId) => {
    try {
      await fetch(`${process.env.REACT_APP_NODEJS_SERVER_URL}/ai/drafts/${draftId}/reject`, {
        method: "POST",
        headers: { accessToken: localStorage.getItem("accessToken") },
      });
      if (draftsJob) fetchDraftsForJob(draftsJob);
    } catch (err) {
      // ignore
    }
  };

  useEffect(() => {
    fetchJobs();
    fetchSummary();
    const interval = setInterval(() => {
      fetchJobs({ silent: true });
      fetchSummary();
    }, 30000);

    return () => clearInterval(interval);
  }, [fetchJobs, fetchSummary]);

  useEffect(() => {
    if (selectedJobId) {
      fetchJobDetail(selectedJobId);
    }
  }, [fetchJobDetail, selectedJobId]);

  const renderStatusBar = () => (
    <Paper className="ai-jobs-status-card" elevation={1}>
      <Stack direction={{ xs: "column", md: "row" }} alignItems={{ xs: "flex-start", md: "center" }} spacing={2}>
        <Stack direction="row" spacing={1} alignItems="center">
          <LiveDot active={Boolean(summary?.running)} />
          <Typography variant="subtitle1" fontWeight={700}>
            Live AI Jobs
          </Typography>
          <Tooltip title="Running jobs represent automations currently executing or waiting on external services." arrow>
            <InfoOutlinedIcon fontSize="small" sx={{ color: "text.secondary" }} />
          </Tooltip>
        </Stack>
        <Stack direction="row" spacing={1} flexWrap="wrap">
          <Tooltip title="Jobs waiting for the worker to pick them up." arrow>
            <Chip label={`Queued: ${summary?.queued ?? '-'} `} size="small" color="info" />
          </Tooltip>
          <Tooltip title="Jobs currently executing." arrow>
            <Chip label={`Running: ${summary?.running ?? '-'} `} size="small" color="primary" />
          </Tooltip>
          <Tooltip title="Failures in the past 24 hours." arrow>
            <Chip label={`Failed (24h): ${summary?.failed24h ?? '-'}`} size="small" color="error" />
          </Tooltip>
          <Tooltip title="Successful jobs in the past 24 hours." arrow>
            <Chip label={`Done (24h): ${summary?.done24h ?? '-'}`} size="small" color="success" />
          </Tooltip>
        </Stack>
        <Box flexGrow={1} />
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="caption" color="textSecondary">
            Last update: {summary?.lastTickAt ? formatDateWithRelative(summary.lastTickAt) : '—'}
          </Typography>
          <Tooltip title="Refresh job feed" arrow>
            <IconButton size="small" onClick={() => { fetchJobs(); fetchSummary(); }}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>
    </Paper>
  );

  const renderMetricTiles = () => (
    <Grid container spacing={1.5}>
      <Grid item xs={12} sm={6} md={3}>
        <MetricCard
          label="Open jobs"
          value={openCount}
          tone="primary"
          icon={<PrecisionManufacturingIcon fontSize="small" />}
          tooltip="Active AI work waiting for action or processing."
        />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <MetricCard
          label="Pending approvals"
          value={pendingApprovalsCount}
          tone="warning"
          icon={<Badge color="warning" variant="dot"><span /></Badge>}
          tooltip="Draft outputs that require human approval before sending or logging."
        />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <MetricCard
          label="Snoozed"
          value={snoozedCount}
          tone="muted"
          icon={<QueryBuilderIcon fontSize="small" />}
          tooltip="Jobs temporarily paused until the snooze timer ends."
        />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <MetricCard
          label="Running"
          value={summary?.running ?? "-"}
          tone="success"
          icon={<LiveDot active />}
          tooltip="Currently executing automations."
        />
      </Grid>
    </Grid>
  );

  const renderFilters = () => (
    <Paper variant="outlined" className="ai-filter-panel">
      <Stack spacing={1.25}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="subtitle2" fontWeight={700}>
            Filter & focus
          </Typography>
          <Tooltip title="Use filters to focus on a specific stage, owner, or approval mode." arrow>
            <InfoOutlinedIcon fontSize="small" sx={{ color: "text.secondary" }} />
          </Tooltip>
        </Stack>
        <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} alignItems="flex-start">
          <TextField
            select
            size="small"
            label={<ColumnHeader label="Status" tooltip="Processing status from the AI worker." />}
            value={filters.status}
            onChange={(e) => setFilters((prev) => ({ ...prev, status: e.target.value }))}
          >
            {statusOptions.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            size="small"
            label={<ColumnHeader label="Job Type" tooltip="Source action or pipeline name." />}
            value={filters.jobType}
            onChange={(e) => setFilters((prev) => ({ ...prev, jobType: e.target.value }))}
          >
            {jobTypeChoices.map((opt) => (
              <MenuItem key={opt.value || "all"} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            size="small"
            label={<ColumnHeader label="Stage" tooltip="Sales lifecycle stage tracked by the rule." />}
            value={filters.stageKey}
            onChange={(e) => setFilters((prev) => ({ ...prev, stageKey: e.target.value }))}
          >
            {stageOptions.map((opt) => (
              <MenuItem key={opt.value || "all"} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            size="small"
            label={<ColumnHeader label="Mode" tooltip="Autonomous jobs execute immediately; Drafts wait for approval." />}
            value={filters.mode}
            onChange={(e) => setFilters((prev) => ({ ...prev, mode: e.target.value }))}
          >
            {modeOptions.map((opt) => (
              <MenuItem key={opt.value || "all"} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            size="small"
            label={<ColumnHeader label="Owner" tooltip="User responsible for acting on this AI recommendation." />}
            value={filters.owner}
            onChange={(e) => setFilters((prev) => ({ ...prev, owner: e.target.value }))}
          >
            {ownerChoices.map((opt) => (
              <MenuItem key={opt.value || "all"} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            size="small"
            label={<ColumnHeader label="State" tooltip="Operational disposition you can control from the row actions." />}
            value={filters.state}
            onChange={(e) => setFilters((prev) => ({ ...prev, state: e.target.value }))}
          >
            {stateOptions.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            size="small"
            label={<ColumnHeader label="Search" tooltip="Match by entity name, dedupe key, rule, or error snippet." />}
            placeholder="Entity, dedupe key, error"
            value={filters.search}
            onChange={(e) => setFilters((prev) => ({ ...prev, search: e.target.value }))}
          />
        </Stack>
        <Divider sx={{ my: 0.5 }} />
        <Stack spacing={0.5}>
          <Typography variant="caption" fontWeight={700} textTransform="uppercase" color="textSecondary">
            Columns
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {columnOptions.map((col) => (
              <FormControlLabel
                key={col.key}
                control={
                  <Checkbox
                    size="small"
                    checked={isColumnVisible(col.key)}
                    onChange={(e) =>
                      setVisibleColumns((prev) => ({
                        ...prev,
                        [col.key]: e.target.checked,
                      }))
                    }
                  />
                }
                label={col.label}
              />
            ))}
          </Stack>
        </Stack>
      </Stack>
    </Paper>
  );

  const renderActions = (job) => (
    <Stack direction="row" spacing={0.75} alignItems="center" justifyContent="flex-end" flexWrap="wrap">
      {/* Keep actions lean; clone is omitted until we support safe duplication flows. */}
      <Tooltip title="Open full job detail" arrow>
        <Button
          variant="outlined"
          size="small"
          startIcon={<LaunchIcon fontSize="small" />}
          onClick={() => setSelectedJobId(job.id)}
        >
          Details
        </Button>
      </Tooltip>
      {job.status === "failed" && (
        <Tooltip title="Retry this automation run" arrow>
          <Button
            variant="contained"
            color="error"
            size="small"
            startIcon={<ReplayIcon fontSize="small" />}
            onClick={() => handleRetry(job.id)}
          >
            Retry
          </Button>
        </Tooltip>
      )}
      {job.status === "queued" && (
        <Tooltip title="Remove from queue" arrow>
          <Button
            variant="outlined"
            color="secondary"
            size="small"
            startIcon={<CancelIcon fontSize="small" />}
            onClick={() => handleCancel(job.id)}
          >
            Cancel
          </Button>
        </Tooltip>
      )}
      <Tooltip title="Resolve when the action is complete" arrow>
        <IconButton size="small" color="success" onClick={() => handleResolve(job.id)}>
          <CheckCircleIcon fontSize="inherit" />
        </IconButton>
      </Tooltip>
      <Tooltip title="Pause notifications for this job" arrow>
        <IconButton size="small" onClick={() => setSnoozeJob(job)}>
          <SnoozeIcon fontSize="inherit" />
        </IconButton>
      </Tooltip>
      <Tooltip title="Route this job to another owner" arrow>
        <IconButton
          size="small"
          onClick={() => {
            setAssignJob(job);
            setAssignOwner(job.ownerUser?.id || "");
          }}
        >
          <LaunchIcon fontSize="inherit" />
        </IconButton>
      </Tooltip>
    </Stack>
  );

  const renderTable = () => (
    <Paper className="ai-table-card" elevation={2}>
      <div className="ai-table-header">
        <div>
          <Typography variant="h6" fontWeight={700} gutterBottom>
            AI Jobs
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Live pipeline visibility for this instance. Rows auto-refresh every ~30s while you watch.
          </Typography>
        </div>
      </div>

      {renderFilters()}

      {(hasLoaded && loading) || isRefreshing ? (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
          <CircularProgress size={16} />
          <Typography variant="body2" color="textSecondary">
            Refreshing jobs...
          </Typography>
        </Stack>
      ) : null}

      <Box sx={{ mt: 1, overflowX: "auto" }}>
        <Table className="ai-actions-table" size="small" sx={{ minWidth: 1100 }}>
          <TableHead>
            <TableRow>
              {isColumnVisible("status") && (
                <TableCell>
                  <ColumnHeader label="Status" tooltip="Worker status for this run." />
                </TableCell>
              )}
              {isColumnVisible("state") && (
                <TableCell>
                  <ColumnHeader label="State" tooltip="Operational state you can control." />
                </TableCell>
              )}
              {isColumnVisible("jobType") && (
                <TableCell>
                  <ColumnHeader label="Job Type" tooltip="Automation family or processor." />
                </TableCell>
              )}
              {isColumnVisible("stage") && (
                <TableCell>
                  <ColumnHeader label="Stage" tooltip="Lifecycle stage the rule monitors." />
                </TableCell>
              )}
              {isColumnVisible("owner") && (
                <TableCell>
                  <ColumnHeader label="Owner" tooltip="Human accountable for action." />
                </TableCell>
              )}
              {isColumnVisible("mode") && (
                <TableCell>
                  <ColumnHeader label="Mode" tooltip="Draft mode pauses outputs until approved." />
                </TableCell>
              )}
              {isColumnVisible("entity") && (
                <TableCell>
                  <ColumnHeader label="Entity" tooltip="Customer, project, estimate, or order in scope." />
                </TableCell>
              )}
              {isColumnVisible("output") && (
                <TableCell>
                  <ColumnHeader label="Output" tooltip="What the AI produced or proposes." />
                </TableCell>
              )}
              {isColumnVisible("runAt") && (
                <TableCell>
                  <ColumnHeader label="Run at" tooltip="Scheduled execution window." />
                </TableCell>
              )}
              {isColumnVisible("attempts") && (
                <TableCell>
                  <ColumnHeader label="Attempts" tooltip="Retry count for this job." />
                </TableCell>
              )}
              {isColumnVisible("email") && (
                <TableCell>
                  <ColumnHeader label="Email" tooltip="Email status when applicable." />
                </TableCell>
              )}
              {isColumnVisible("updated") && (
                <TableCell>
                  <ColumnHeader label="Updated" tooltip="Last touch from the worker or approval." />
                </TableCell>
              )}
              {isColumnVisible("actions") && (
                <TableCell align="right">
                  <ColumnHeader label="Actions" tooltip="Resolve, snooze, assign, or retry." />
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && !hasLoaded ? (
              <TableRow>
                <TableCell colSpan={visibleColumnCount}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <CircularProgress size={18} />
                    <Typography variant="body2" color="textSecondary">
                      Loading jobs...
                    </Typography>
                  </Stack>
                </TableCell>
              </TableRow>
            ) : jobs.length === 0 && hasLoaded ? (
              <TableRow>
                <TableCell colSpan={visibleColumnCount}>
                  <Typography variant="body2" color="textSecondary">
                    No jobs found for this filter.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              jobs.map((job) => {
                const rowClass =
                  job.status === "running"
                    ? "ai-job-row-running"
                    : job.state === "snoozed"
                    ? "ai-job-row-snoozed"
                    : job.state === "resolved"
                    ? "ai-job-row-resolved"
                    : "ai-job-row";
                return (
                  <TableRow key={job.id} className={rowClass} hover>
                    {isColumnVisible("status") && (
                      <TableCell>
                        <StatusPill status={job.status} />
                        {job.isStaleLock && (
                          <Tooltip title="This job looks locked for too long.">
                            <WarningAmberIcon color="warning" fontSize="small" sx={{ ml: 0.5 }} />
                          </Tooltip>
                        )}
                      </TableCell>
                    )}
                    {isColumnVisible("state") && (
                      <TableCell>
                        <Tooltip
                          arrow
                          title="State controls your operational workflow (resolve when done, snooze if waiting on someone)."
                        >
                          <Chip
                            size="small"
                            label={job.state || "open"}
                            color={stateColors[job.state || "open"] || "default"}
                            variant={job.state === "resolved" ? "filled" : "outlined"}
                          />
                        </Tooltip>
                        {job.snoozedUntil && (
                          <Typography variant="caption" color="textSecondary" display="block">
                            Snoozed until {formatDateWithRelative(job.snoozedUntil)}
                          </Typography>
                        )}
                      </TableCell>
                    )}
                    {isColumnVisible("jobType") && <TableCell>{job.jobType}</TableCell>}
                    {isColumnVisible("stage") && (
                      <TableCell>
                        <Tooltip
                          arrow
                          title={stageTooltips[job.stageKey] || "Sales stage targeted by this rule."}
                        >
                          <Chip size="small" label={job.stageKey || "-"} variant="outlined" />
                        </Tooltip>
                      </TableCell>
                    )}
                    {isColumnVisible("owner") && (
                      <TableCell>
                        {job.ownerUser ? (
                          <Stack spacing={0.25}>
                            <Typography variant="body2">{job.ownerUser.name}</Typography>
                            <Typography variant="caption" color="textSecondary">
                              {job.ownerUser.role || job.ownerUser.email}
                            </Typography>
                          </Stack>
                        ) : (
                          "—"
                        )}
                      </TableCell>
                    )}
                    {isColumnVisible("mode") && (
                      <TableCell>
                        <Tooltip
                          arrow
                          title={
                            job.mode === "draft"
                              ? "Draft mode captures outputs for approval before anything is sent."
                              : "Autonomous mode executes tasks immediately."
                          }
                        >
                          <Chip
                            size="small"
                            label={job.mode}
                            color={job.mode === "draft" ? "warning" : "default"}
                          />
                        </Tooltip>
                      </TableCell>
                    )}
                    {isColumnVisible("entity") && (
                      <TableCell>
                        <EntityCell job={job} />
                      </TableCell>
                    )}
                    {isColumnVisible("output") && (
                      <TableCell>
                        <Stack spacing={0.5}>
                          {job.ruleKey && (
                            <Tooltip title="Automation rule that created this job." arrow>
                              <Chip
                                label={job.ruleKey}
                                size="small"
                                variant="outlined"
                                color="info"
                                className="ai-rule-chip"
                              />
                            </Tooltip>
                          )}
                          <Tooltip title={job.resultSummary || "No summary captured yet."} arrow>
                            <Typography variant="body2" color="textSecondary" className="ai-result-summary">
                              {job.resultSummary || "(no summary)"}
                            </Typography>
                          </Tooltip>
                          <Stack direction="row" spacing={1}>
                            <Button variant="text" size="small" onClick={() => setSelectedJobId(job.id)}>
                              View Summary
                            </Button>
                            {(job.hasPendingDrafts || job.requiresApproval) && (
                              <Tooltip title="Draft outputs are waiting for a reviewer." arrow>
                                <Chip label="Pending Approval" color="warning" size="small" />
                              </Tooltip>
                            )}
                            <Tooltip title="Open drafts and approvals for this job." arrow>
                              <span>
                                <Button
                                  variant="outlined"
                                  size="small"
                                  onClick={() => fetchDraftsForJob(job)}
                                  disabled={!job.hasPendingDrafts && !job.requiresApproval && job.mode !== "draft"}
                                >
                                  View Drafts
                                </Button>
                              </span>
                            </Tooltip>
                          </Stack>
                        </Stack>
                      </TableCell>
                    )}
                    {isColumnVisible("runAt") && <TableCell>{formatDateWithRelative(job.runAt)}</TableCell>}
                    {isColumnVisible("attempts") && <TableCell>{job.attempts}</TableCell>}
                    {isColumnVisible("email") && <TableCell>{job.emailStatus || "-"}</TableCell>}
                    {isColumnVisible("updated") && <TableCell>{formatDateWithRelative(job.updatedAt)}</TableCell>}
                    {isColumnVisible("actions") && <TableCell align="right">{renderActions(job)}</TableCell>}
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </Box>
      {error && (
        <Typography variant="caption" color="error">
          {error}
        </Typography>
      )}
      <Typography variant="caption" color="textSecondary">
        Showing {jobs.length} of {jobCount} jobs.
      </Typography>
    </Paper>
  );

  const renderDetailDialog = () => (
    <Dialog open={Boolean(selectedJobId)} onClose={() => setSelectedJobId(null)} fullWidth maxWidth="md">
      <DialogTitle>AI Job {selectedJobId}</DialogTitle>
      <DialogContent dividers>
        {isDetailLoading && (
          <Stack direction="row" spacing={1} alignItems="center">
            <CircularProgress size={18} />
            <Typography variant="body2">Loading job...</Typography>
          </Stack>
        )}
        {!isDetailLoading && selectedJob && (
          <Stack spacing={1.5}>
            <Stack direction="row" spacing={1} alignItems="center">
              <StatusPill status={selectedJob.status} />
              <Typography variant="body2" color="textSecondary">
                {selectedJob.jobType}
              </Typography>
              {selectedJob.isStaleLock && (
                <Chip
                  size="small"
                  color="warning"
                  icon={<WarningAmberIcon fontSize="small" />}
                  label="Stale lock"
                />
              )}
            </Stack>
            <Stack spacing={0.5}>
              <Typography variant="body2" fontWeight={700}>{formatEntityTitle(selectedJob)}</Typography>
              <Typography variant="body2" color="textSecondary">
                {formatEntitySubtitle(selectedJob)}
              </Typography>
              {selectedJob.entityMetadata?.customer && (
                <Typography variant="body2" color="textSecondary">
                  {joinMeta([
                    selectedJob.entityMetadata.customer.status,
                    selectedJob.entityMetadata.customer.leadSource
                      ? `Source: ${selectedJob.entityMetadata.customer.leadSource}`
                      : "",
                  ])}
                </Typography>
              )}
              {selectedJob.entityMetadata?.customer?.emailAddress && (
                <Typography variant="body2" color="textSecondary">
                  {selectedJob.entityMetadata.customer.contactName
                    ? `${selectedJob.entityMetadata.customer.contactName} • `
                    : ""}
                  {selectedJob.entityMetadata.customer.emailAddress}
                  {selectedJob.entityMetadata.customer.phoneNumber
                    ? ` • ${selectedJob.entityMetadata.customer.phoneNumber}`
                    : ""}
                </Typography>
              )}
              <AssignedAvatars users={selectedJob.entityMetadata?.assignedUsers} />
            </Stack>
            <Typography variant="body2">Run at: {formatDateWithRelative(selectedJob.runAt)}</Typography>
            <Typography variant="body2">Updated: {formatDateWithRelative(selectedJob.updatedAt)}</Typography>
            <Typography variant="body2">Attempts: {selectedJob.attempts}</Typography>
            <Typography variant="body2">State: {selectedJob.state || "open"}</Typography>
            {selectedJob.snoozedUntil && (
              <Typography variant="body2" color="textSecondary">
                Snoozed until {formatDateWithRelative(selectedJob.snoozedUntil)}
              </Typography>
            )}
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
              <Chip size="small" label={`Stage: ${selectedJob.stageKey || "n/a"}`} />
              <Chip size="small" label={`Mode: ${selectedJob.mode || "n/a"}`} />
              {selectedJob.ownerUser?.name && (
                <Chip size="small" label={`Owner: ${selectedJob.ownerUser.name}`} />
              )}
              {selectedJob.resolvedBy?.name && (
                <Chip size="small" color="success" label={`Resolved by: ${selectedJob.resolvedBy.name}`} />
              )}
              {selectedJob.ruleKey && (
                <Chip size="small" color="info" label={`Rule: ${selectedJob.ruleKey}`} />
              )}
            </Stack>
            {selectedJob.resultSummary && (
              <Paper className="ai-job-detail-card" variant="outlined">
                <Typography variant="subtitle2" gutterBottom>
                  Result summary
                </Typography>
                <Typography variant="body2">{selectedJob.resultSummary}</Typography>
              </Paper>
            )}
            {selectedJob.resultPayload && (
              <Paper className="ai-job-detail-card" variant="outlined">
                <Typography variant="subtitle2" gutterBottom>
                  Report
                </Typography>
                <pre className="ai-code-block">{prettyJson(selectedJob.resultPayload)}</pre>
              </Paper>
            )}
            {selectedJob.lastError && (
              <Paper className="ai-job-detail-card" variant="outlined">
                <Typography variant="subtitle2" color="error" gutterBottom>
                  Last error
                </Typography>
                <Typography variant="body2">{selectedJob.lastError}</Typography>
              </Paper>
            )}
            <Divider />
            <Typography variant="subtitle2">Payload</Typography>
            <Paper className="ai-job-detail-card" variant="outlined">
              <pre className="ai-code-block">{prettyJson(selectedJob.payload)}</pre>
            </Paper>
            <Typography variant="subtitle2">Result</Typography>
            <Paper className="ai-job-detail-card" variant="outlined">
              <pre className="ai-code-block">{prettyJson(selectedJob.result)}</pre>
            </Paper>
            <Divider />
            <Stack direction="row" spacing={1}>
              <Button
                variant="text"
                size="small"
                endIcon={<LaunchIcon fontSize="small" />}
                href={`/instance/${instanceUniqueId}/ai/settings`}
              >
                Open AI Settings
              </Button>
              {selectedJob.entityType === "customer" && selectedJob.entityId && (
                <Button
                  variant="text"
                  size="small"
                  endIcon={<LaunchIcon fontSize="small" />}
                  href={`/instance/${instanceUniqueId}/sales/customers/?tab=all&openCustomer=${selectedJob.entityId}`}
                >
                  View customer
                </Button>
              )}
            </Stack>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setSelectedJobId(null)}>Close</Button>
      </DialogActions>
    </Dialog>
  );

  const renderDraftDialog = () => (
    <Dialog open={Boolean(draftsJob)} onClose={() => setDraftsJob(null)} fullWidth maxWidth="sm">
      <DialogTitle>Draft outputs for job {draftsJob?.id}</DialogTitle>
      <DialogContent dividers>
        {isDraftsLoading && (
          <Stack direction="row" spacing={1} alignItems="center">
            <CircularProgress size={18} />
            <Typography variant="body2">Loading drafts...</Typography>
          </Stack>
        )}
        {!isDraftsLoading && draftsError && (
          <Typography variant="body2" color="error">
            {draftsError}
          </Typography>
        )}
        {!isDraftsLoading && !draftsError && drafts.length === 0 && (
          <Typography variant="body2" color="textSecondary">
            No drafts yet for this job.
          </Typography>
        )}
        {!isDraftsLoading && !draftsError && drafts.length > 0 && (
          <Stack spacing={1.5}>
            {drafts.map((draft) => (
              <Paper key={draft.id} variant="outlined" sx={{ p: 1 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Stack spacing={0.25}>
                    <Typography variant="body2" fontWeight={700}>
                      {draft.type}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      Status: {draft.status}
                    </Typography>
                  </Stack>
                  {draft.status === "pending" && (
                    <Stack direction="row" spacing={1}>
                      <Button size="small" variant="contained" onClick={() => handleApproveDraft(draft.id)}>
                        Approve
                      </Button>
                      <Button size="small" variant="outlined" color="error" onClick={() => handleRejectDraft(draft.id)}>
                        Reject
                      </Button>
                    </Stack>
                  )}
                </Stack>
                {draft.payload && (
                  <pre className="ai-code-block" style={{ marginTop: 8 }}>
                    {prettyJson(draft.payload)}
                  </pre>
                )}
              </Paper>
            ))}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setDraftsJob(null)}>Close</Button>
      </DialogActions>
    </Dialog>
  );

  const renderSnoozeDialog = () => (
    <Dialog open={Boolean(snoozeJob)} onClose={() => setSnoozeJob(null)}>
      <DialogTitle>Snooze job {snoozeJob?.id}</DialogTitle>
      <DialogContent dividers>
        <TextField
          select
          fullWidth
          label="Snooze duration"
          value={snoozeHours}
          onChange={(e) => setSnoozeHours(Number(e.target.value))}
          sx={{ mt: 1, minWidth: 260 }}
        >
          {[7, 24, 72].map((hours) => (
            <MenuItem key={hours} value={hours}>
              {hours} hours
            </MenuItem>
          ))}
        </TextField>
        {snoozeJob?.snoozedUntil && (
          <Typography variant="caption" color="textSecondary" display="block" sx={{ mt: 1 }}>
            Currently snoozed until {formatDateWithRelative(snoozeJob.snoozedUntil)}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setSnoozeJob(null)}>Cancel</Button>
        <Button onClick={handleSnoozeSubmit} variant="contained">
          Snooze
        </Button>
      </DialogActions>
    </Dialog>
  );

  const renderAssignDialog = () => (
    <Dialog open={Boolean(assignJob)} onClose={() => setAssignJob(null)}>
      <DialogTitle>Assign job {assignJob?.id}</DialogTitle>
      <DialogContent dividers>
        <TextField
          select
          fullWidth
          label="Owner"
          value={assignOwner}
          onChange={(e) => setAssignOwner(e.target.value)}
          sx={{ mt: 1, minWidth: 260 }}
        >
          {ownerChoices
            .filter((o) => o.value !== "")
            .map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
        </TextField>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setAssignJob(null)}>Cancel</Button>
        <Button onClick={handleAssignSubmit} variant="contained" disabled={!assignOwner}>
          Assign
        </Button>
      </DialogActions>
    </Dialog>
  );

  return (
    <Stack spacing={1.5}>
      {renderStatusBar()}
      {renderMetricTiles()}
      {renderTable()}
      {renderDetailDialog()}
      {renderDraftDialog()}
      {renderSnoozeDialog()}
      {renderAssignDialog()}
    </Stack>
  );
};

export default AiJobsTab;
